import { useCallback, useState, useEffect } from 'react';
import shallow from 'zustand/shallow';
import type { Site } from '@hotjar/site';
import {
  useGSGStore,
  selectStoreById,
} from '@hotjar/webapp/domains/activation/getting-started-guide';
import type { CurrentUser } from '@hotjar/user';
import type { Step, GettingStartedGuideState } from '../types';
import { StepState } from '../types';
import { useSteps } from './useSteps';

export interface StepsInfo {
  steps: Array<Step>;
  total: number;
  remaining: number;
  expandedStepIndex: number | undefined;
  isStepsReady: boolean;
  setExpandedStepIndex: (index: number | undefined) => void;
}

export function useGettingStartedState(site?: Site, user?: CurrentUser): StepsInfo {
  const gettingStartedGuideState = useGSGStore(
    useCallback((state) => selectStoreById(state, site), [site]),
    shallow,
  ) as GettingStartedGuideState;
  const { steps, isStepsReady } = useSteps(gettingStartedGuideState, user);
  const total = steps.length;
  const remaining = steps.filter((step) => step.state === StepState.Pending).length;
  const getStepIndex = () => {
    const firstPendingStep = steps.find((step: Step) => step.state === StepState.Pending);
    const firstPendingStepIndex = firstPendingStep ? steps.indexOf(firstPendingStep) : -1;

    return firstPendingStepIndex > -1 ? firstPendingStepIndex : undefined;
  };

  const [expandedStepIndex, setExpandedStepIndex] = useState<number | undefined>(getStepIndex());

  useEffect(() => {
    setExpandedStepIndex(getStepIndex());
    // we only want to set index when isStepsReady changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStepsReady]);

  return {
    steps,
    total,
    remaining,
    expandedStepIndex,
    setExpandedStepIndex,
    isStepsReady,
  };
}
